import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Send } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { API_ENDPOINTS, buildApiUrl } from "../../config/apiConfig";
import { Button } from "../ui/button";

export default function LandingQuestionPrompt() {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const isRtl = dir === "rtl";
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, enterGuestMode } = useAuth();

  const [question, setQuestion] = useState("");
  const [emptyError, setEmptyError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = question.trim();

    if (!trimmed) {
      setEmptyError(t("landingQuestion.emptyError"));
      return;
    }

    setEmptyError("");
    setSubmitError("");

    if (isLoading || submitting) return;

    setSubmitting(true);
    try {
      if (!isAuthenticated) {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.AUTH_GUEST), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.user?.isGuest) {
          throw new Error(data?.message || "Failed to start guest session");
        }
        enterGuestMode(data.user);
      }

      navigate(`/disc?q=${encodeURIComponent(trimmed)}`);
    } catch (err) {
      if (err?.message) setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const busy = isLoading || submitting;

  return (
    <form
      className="landing-question-prompt mt-8 w-full max-w-2xl mx-auto lg:mx-0"
      onSubmit={handleSubmit}
      dir={dir}
    >
      <label htmlFor="landing-question-input" className="sr-only">
        {t("landingQuestion.placeholder")}
      </label>
      <textarea
        id="landing-question-input"
        className="landing-question-prompt__textarea"
        rows={3}
        value={question}
        onChange={(e) => {
          setQuestion(e.target.value);
          if (emptyError) setEmptyError("");
          if (submitError) setSubmitError("");
        }}
        placeholder={t("landingQuestion.placeholder")}
        disabled={busy}
      />
      <p className="landing-question-prompt__helper">{t("landingQuestion.helper")}</p>
      {emptyError ? (
        <p className="landing-question-prompt__error" role="alert">
          {emptyError}
        </p>
      ) : null}
      {submitError ? (
        <p className="landing-question-prompt__error" role="alert">
          {submitError}
        </p>
      ) : null}
      <div className="flex justify-center lg:justify-start">
        <Button
          type="submit"
          size="lg"
          className="landing-btn-primary text-base h-11 px-6 w-full sm:w-auto"
          disabled={busy}
        >
          {t("landingQuestion.submit")}
          {isRtl ? (
            <ArrowLeft className="ml-2 h-4 w-4" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
        </Button>
      </div>
    </form>
  );
}
